import { Search, Plus, Eye, FileText, CreditCard, ChevronLeft, ChevronRight } from "lucide-react";
import { Link, useSearchParams } from "react-router";
import { createPortal } from "react-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useGetClientConfigurationCustgroupsDirectoryQuery,
  useGetClientConfigurationCustomerJacketJobsQuery,
  useGetClientConfigurationCustomerPoOptionsQuery,
} from "../store";

const PAGE_SIZE = 20;
/** Brief grace period when moving from trigger link to anchored popup panel. */
const HOVER_POPUP_BRIDGE_MS = 120;

function anchorHoverPopup(triggerEl) {
  const rect = triggerEl.getBoundingClientRect();
  return {
    top: rect.bottom + 4,
    left: rect.left,
    minWidth: Math.max(rect.width, 240),
  };
}

function createHoverPopupController(close) {
  const zone = { trigger: false, panel: false };
  let bridgeTimer = null;

  const clearBridgeTimer = () => {
    if (bridgeTimer != null) {
      window.clearTimeout(bridgeTimer);
      bridgeTimer = null;
    }
  };

  const closeIfOutside = () => {
    if (!zone.trigger && !zone.panel) {
      close();
    }
  };

  return {
    reset() {
      clearBridgeTimer();
      zone.trigger = false;
      zone.panel = false;
    },
    onTriggerEnter() {
      clearBridgeTimer();
      zone.trigger = true;
    },
    onTriggerLeave() {
      zone.trigger = false;
      clearBridgeTimer();
      bridgeTimer = window.setTimeout(() => {
        bridgeTimer = null;
        closeIfOutside();
      }, HOVER_POPUP_BRIDGE_MS);
    },
    onPanelEnter() {
      clearBridgeTimer();
      zone.panel = true;
    },
    onPanelLeave() {
      clearBridgeTimer();
      zone.trigger = false;
      zone.panel = false;
      close();
    },
  };
}

/** Canonical positive customer id for routes and customer-scoped APIs. */
function parsePositiveCustId(value) {
  const n = Number.parseInt(String(value ?? ""), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function clientCustPath(client) {
  const custId = parsePositiveCustId(client?.custId);
  return custId != null ? `/clients/${custId}` : null;
}
function ClientsLoadingDots() {
  return (
    <span className="ml-1 inline-flex align-middle" aria-hidden="true">
      <span className="relative inline-block h-3.5 w-3.5 animate-spin text-current [animation-duration:1.15s]">
        <span className="absolute left-1/2 top-0 block h-1 w-1 -translate-x-1/2 rounded-full bg-current" />
        <span className="absolute bottom-0 left-0 block h-1 w-1 rounded-full bg-current" />
        <span className="absolute bottom-0 right-0 block h-1 w-1 rounded-full bg-current" />
      </span>
    </span>
  );
}

function formatJacketsLoadError(err) {
  if (!err) {
    return "Unable to load jackets.";
  }
  const data = err?.data;
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail.trim();
  }
  if (typeof err?.error === "string" && err.error.trim()) {
    return err.error.trim();
  }
  if (err?.status === "FETCH_ERROR") {
    return "Network error loading jackets. Check that the API server is running.";
  }
  if (typeof err?.status === "number") {
    return `Unable to load jackets (HTTP ${err.status}).`;
  }
  return "Unable to load jackets.";
}

function formatPosLoadError(err) {
  if (!err) {
    return "Unable to load purchase orders.";
  }
  const data = err?.data;
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail.trim();
  }
  if (typeof err?.error === "string" && err.error.trim()) {
    return err.error.trim();
  }
  if (err?.status === "FETCH_ERROR") {
    return "Network error loading purchase orders. Check that the API server is running.";
  }
  if (typeof err?.status === "number") {
    return `Unable to load purchase orders (HTTP ${err.status}).`;
  }
  return "Unable to load purchase orders.";
}

function Clients() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get("search") ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    (searchParams.get("search") ?? "").trim()
  );
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") ?? "all");
  const [page, setPage] = useState(() => {
    const p = Number.parseInt(searchParams.get("page") ?? "1", 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  });

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const q = debouncedSearch.trim();
        if (q) {
          next.set("search", q);
        } else {
          next.delete("search");
        }
        if (statusFilter && statusFilter !== "all") {
          next.set("status", statusFilter);
        } else {
          next.delete("status");
        }
        if (page > 1) {
          next.set("page", String(page));
        } else {
          next.delete("page");
        }
        return next;
      },
      { replace: true }
    );
  }, [debouncedSearch, statusFilter, page, setSearchParams]);

  const directoryArgs = useMemo(
    () => ({
      page,
      size: PAGE_SIZE,
      search: debouncedSearch,
      status: statusFilter,
    }),
    [page, debouncedSearch, statusFilter]
  );

  const {
    data: directoryLite,
    isLoading: isDirectoryLiteLoading,
    isError,
  } = useGetClientConfigurationCustgroupsDirectoryQuery({
    ...directoryArgs,
    includeCounts: false,
  });
  const { data: directoryFull, isFetching: isDirectoryCountsFetching } =
    useGetClientConfigurationCustgroupsDirectoryQuery(
      { ...directoryArgs, includeCounts: true },
      { skip: isDirectoryLiteLoading }
    );

  const directory = directoryFull ?? directoryLite;
  const pagedRows = directory?.items ?? [];
  const totalFiltered = directory?.totalFiltered ?? 0;
  const totalClients = directory?.totalClients ?? 0;
  const activeClients = directory?.activeClients ?? 0;
  const totalActivePos = directory?.totalActivePoCount ?? 0;
  const countsPending = isDirectoryCountsFetching && !directoryFull;
  const isLoading = isDirectoryLiteLoading;
  const [jacketBox, setJacketBox] = useState(null);
  const [poBox, setPoBox] = useState(null);
  const jacketHoverRef = useRef(null);
  const poHoverRef = useRef(null);

  if (!jacketHoverRef.current) {
    jacketHoverRef.current = createHoverPopupController(() => setJacketBox(null));
  }
  if (!poHoverRef.current) {
    poHoverRef.current = createHoverPopupController(() => setPoBox(null));
  }

  const closeJacketLightbox = useCallback(() => {
    jacketHoverRef.current?.reset();
    setJacketBox(null);
  }, []);

  const closePoLightbox = useCallback(() => {
    poHoverRef.current?.reset();
    setPoBox(null);
  }, []);

  const jacketCustId = parsePositiveCustId(jacketBox?.custId);
  const {
    data: jacketJobsPayload,
    isFetching: isJacketsFetching,
    isError: isJacketsError,
    error: jacketsError,
  } = useGetClientConfigurationCustomerJacketJobsQuery(jacketCustId, {
    skip: jacketCustId == null,
  });
  const jacketJobNos = useMemo(() => {
    const jobs = jacketJobsPayload?.jobs ?? [];
    const set = new Set();
    for (const job of jobs) {
      const no = String(job?.jobNo ?? job?.jobNumber ?? "").trim();
      if (no) {
        set.add(no);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [jacketJobsPayload]);

  const poCustId = parsePositiveCustId(poBox?.custId);
  const {
    data: poOptionsRaw = [],
    isFetching: isPosFetching,
    isError: isPosError,
    error: posError,
  } = useGetClientConfigurationCustomerPoOptionsQuery(poCustId, { skip: poCustId == null });
  const activePos = useMemo(() => {
    const arr = Array.isArray(poOptionsRaw) ? poOptionsRaw : [];
    return arr
      .map((row) => {
        const poId = row?.poId ?? row?.po_id;
        const clientPo = String(row?.clientPo ?? row?.client_po ?? "").trim();
        if (poId == null || poId === "" || !clientPo) {
          return null;
        }
        return { poId, clientPo };
      })
      .filter(Boolean);
  }, [poOptionsRaw]);

  useEffect(() => {
    if (!jacketBox) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        closeJacketLightbox();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [jacketBox, closeJacketLightbox]);

  const totalPages = Math.ceil(totalFiltered / PAGE_SIZE) || 0;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  const rangeStart = totalFiltered === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = totalFiltered === 0 ? 0 : Math.min(page * PAGE_SIZE, totalFiltered);

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100">Clients</h1>
      </div>

      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Clients</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
        <Link
          to="/clients/new"
          className="app-btn-gloss whitespace-nowrap"
        >
          <Plus className="w-5 h-5" />
          Add Client
        </Link>
      </div>

      {isError ? (
        <p className="mb-4 text-sm text-red-600 dark:text-red-400">
          Unable to load clients from the server. Confirm you are logged in and the API is reachable.
        </p>
      ) : null}

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Client Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Client Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Cust Group ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Active POs
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Jackets
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Total Revenue YTD
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-sm font-bold text-red-600 dark:text-red-400">
                    <span className="inline-flex items-center justify-center gap-0.5">
                      <span>Loading clients</span>
                      <ClientsLoadingDots />
                    </span>
                  </td>
                </tr>
              ) : (
                pagedRows.map((client) => {
                  const custId = parsePositiveCustId(client.custId);
                  const rowKey =
                    custId != null ? `cust-${custId}` : `grp-${client.clientCode || ""}`;
                  const custPath = clientCustPath(client);
                  return (
                    <tr key={rowKey} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                        {client.clientName || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                        {client.clientCode || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 tabular-nums">
                        {custId != null ? custId : "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                        {client.status || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 text-center align-top">
                        <div className="flex flex-col items-center gap-1">
                          <span className="tabular-nums">
                            {countsPending
                              ? "…"
                              : typeof client.activePoCount === "number"
                                ? client.activePoCount
                                : "—"}
                          </span>
                          {!countsPending &&
                          typeof client.activePoCount === "number" &&
                          client.activePoCount > 0 &&
                          custId != null ? (
                            <button
                              type="button"
                              className="app-btn-plain text-xs font-medium text-blue-600 underline decoration-blue-600/40 underline-offset-2 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                              onMouseEnter={(e) => {
                                closeJacketLightbox();
                                poHoverRef.current?.onTriggerEnter();
                                setPoBox({
                                  clientName: client.clientName || "",
                                  clientCode: (client.clientCode || "").trim(),
                                  custId,
                                  anchor: anchorHoverPopup(e.currentTarget),
                                });
                              }}
                              onMouseLeave={() => poHoverRef.current?.onTriggerLeave()}
                            >
                              View POs
                            </button>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 text-center align-top">
                        <div className="flex flex-col items-center gap-1">
                          <span className="tabular-nums">
                            {countsPending
                              ? "…"
                              : typeof client.jacketCount === "number"
                                ? client.jacketCount
                                : "—"}
                          </span>
                          {!countsPending &&
                          typeof client.jacketCount === "number" &&
                          client.jacketCount > 0 &&
                          custId != null ? (
                            <button
                              type="button"
                              className="app-btn-plain text-xs font-medium text-blue-600 underline decoration-blue-600/40 underline-offset-2 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                              onMouseEnter={(e) => {
                                closePoLightbox();
                                jacketHoverRef.current?.onTriggerEnter();
                                setJacketBox({
                                  clientName: client.clientName || "",
                                  clientCode: (client.clientCode || "").trim(),
                                  custId,
                                  anchor: anchorHoverPopup(e.currentTarget),
                                });
                              }}
                              onMouseLeave={() => jacketHoverRef.current?.onTriggerLeave()}
                            >
                              View Jackets
                            </button>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">—</td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-2">
                          {custPath ? (
                            <Link
                              to={custPath}
                              className="app-btn-plain inline-flex items-center gap-1 px-3 py-1 text-xs border border-gray-300 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-900"
                              title="View Client"
                            >
                              <Eye className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                              <span className="text-gray-900 dark:text-gray-100">View</span>
                            </Link>
                          ) : (
                            <span
                              className="px-3 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded text-gray-400 dark:text-gray-500 cursor-not-allowed inline-flex items-center gap-1"
                              title="No customer id — open after a customer record exists"
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </span>
                          )}
                          {custPath ? (
                            <Link
                              to={`${custPath}?tab=contracts`}
                              className="app-btn-plain inline-flex items-center gap-1 px-3 py-1 text-xs border border-gray-300 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-900"
                              title="Manage Contracts"
                            >
                              <CreditCard className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                              <span className="text-gray-900 dark:text-gray-100">Contracts</span>
                            </Link>
                          ) : (
                            <span
                              className="px-3 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded text-gray-400 dark:text-gray-500 cursor-not-allowed inline-flex items-center gap-1"
                              title="No customer id — open after a customer record exists"
                            >
                              <CreditCard className="w-3 h-3" />
                              Contracts
                            </span>
                          )}
                          {custPath ? (
                            <Link
                              to={`${custPath}?tab=billing-profiles`}
                              className="app-btn-plain inline-flex items-center gap-1 px-3 py-1 text-xs border border-gray-300 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-900"
                              title="Invoice profile configuration for this client"
                            >
                              <FileText className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                              <span className="text-gray-900 dark:text-gray-100">Invoices</span>
                            </Link>
                          ) : (
                            <span
                              className="px-3 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded text-gray-400 dark:text-gray-500 cursor-not-allowed inline-flex items-center gap-1"
                              title="No customer id — open after a customer record exists"
                            >
                              <FileText className="w-3 h-3" />
                              Invoices
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
              {!isLoading && totalFiltered === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No clients match your filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {!isLoading && totalFiltered > 0 ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/50">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Showing <span className="font-medium text-gray-900 dark:text-gray-100">{rangeStart}</span>
              –<span className="font-medium text-gray-900 dark:text-gray-100">{rangeEnd}</span> of{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">{totalFiltered}</span>
              {totalFiltered !== totalClients ? (
                <span className="text-gray-500 dark:text-gray-500"> (filtered)</span>
              ) : null}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="app-btn-gloss px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400 px-2 tabular-nums">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="app-btn-gloss px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {jacketBox
        ? createPortal(
            <div
              className="fixed z-[200] before:absolute before:-top-2 before:left-0 before:right-0 before:h-2 before:content-['']"
              style={{
                top: jacketBox.anchor?.top ?? 0,
                left: jacketBox.anchor?.left ?? 0,
                minWidth: jacketBox.anchor?.minWidth ?? 280,
                maxWidth: "min(96vw, 42rem)",
              }}
              role="dialog"
              aria-modal="false"
              aria-labelledby="jacket-lightbox-title"
              onMouseEnter={() => jacketHoverRef.current?.onPanelEnter()}
              onMouseLeave={() => jacketHoverRef.current?.onPanelLeave()}
            >
              <div className="flex max-h-[min(70vh,420px)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-600 dark:bg-gray-900">
                <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
                  <h2
                    id="jacket-lightbox-title"
                    className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Jackets
                  </h2>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {(jacketBox.clientName || "—") +
                      (jacketBox.clientCode ? ` · ${jacketBox.clientCode}` : "") +
                      (jacketBox.custId != null ? ` · #${jacketBox.custId}` : "")}
                  </p>
                </div>
                <div className="min-h-[80px] flex-1 overflow-y-auto p-5">
                  {isJacketsFetching ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading jackets…</p>
                  ) : isJacketsError ? (
                    <p className="text-sm text-red-600 dark:text-red-400">{formatJacketsLoadError(jacketsError)}</p>
                  ) : jacketJobNos.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No job numbers returned.</p>
                  ) : (
                    <div
                      className="grid gap-2"
                      style={{ gridTemplateColumns: "repeat(10, minmax(0, 1fr))" }}
                    >
                      {jacketJobNos.map((jobNo) => {
                        const linkCustId = parsePositiveCustId(jacketBox?.custId);
                        const to =
                          linkCustId != null
                            ? `/clients/${linkCustId}?tab=jackets&jobNo=${encodeURIComponent(String(jobNo))}`
                            : null;
                        return to ? (
                          <Link
                            key={jobNo}
                            to={to}
                            onClick={closeJacketLightbox}
                            className="truncate rounded-md border border-gray-200 bg-gray-50 px-1.5 py-2 text-center text-xs font-medium text-blue-700 underline decoration-blue-600/40 underline-offset-2 hover:bg-gray-100 hover:text-blue-800 dark:border-gray-600 dark:bg-gray-800 dark:text-blue-300 dark:hover:bg-gray-700 dark:hover:text-blue-200"
                            title={`Open jacket ${jobNo} for this client`}
                          >
                            {jobNo}
                          </Link>
                        ) : (
                          <div
                            key={jobNo}
                            className="truncate rounded-md border border-gray-200 bg-gray-50 px-1.5 py-2 text-center text-xs font-medium text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            title={`${jobNo} — customer id required (use View on Clients).`}
                          >
                            {jobNo}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {poBox
        ? createPortal(
            <div
              className="fixed z-[200] before:absolute before:-top-2 before:left-0 before:right-0 before:h-2 before:content-['']"
              style={{
                top: poBox.anchor?.top ?? 0,
                left: poBox.anchor?.left ?? 0,
                minWidth: poBox.anchor?.minWidth ?? 240,
                maxWidth: "min(96vw, 28rem)",
              }}
              role="dialog"
              aria-modal="false"
              aria-labelledby="po-lightbox-title"
              onMouseEnter={() => poHoverRef.current?.onPanelEnter()}
              onMouseLeave={() => poHoverRef.current?.onPanelLeave()}
            >
              <div className="flex max-h-[min(70vh,420px)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-600 dark:bg-gray-900">
                <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
                  <h2
                    id="po-lightbox-title"
                    className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Active purchase orders
                  </h2>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {(poBox.clientName || "—") +
                      (poBox.clientCode ? ` · ${poBox.clientCode}` : "") +
                      (poBox.custId != null ? ` · #${poBox.custId}` : "")}
                  </p>
                </div>
                <div className="min-h-[80px] flex-1 overflow-y-auto p-5">
                  {isPosFetching ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading POs…</p>
                  ) : isPosError ? (
                    <p className="text-sm text-red-600 dark:text-red-400">{formatPosLoadError(posError)}</p>
                  ) : activePos.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No active PO numbers returned.</p>
                  ) : (
                    <ul className="flex flex-col gap-2">
                      {activePos.map((po) => {
                        const clientPo = po.clientPo;
                        const poKey = po.poId != null ? `po-${po.poId}` : clientPo;
                        const linkCustId = parsePositiveCustId(poBox?.custId);
                        const to =
                          linkCustId != null
                            ? `/clients/${linkCustId}/po-jackets?poNumber=${encodeURIComponent(clientPo)}`
                            : null;
                        return (
                          <li key={poKey}>
                            {to ? (
                              <Link
                                to={to}
                                onClick={closePoLightbox}
                                className="block truncate rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-blue-700 underline decoration-blue-600/40 underline-offset-2 hover:bg-gray-100 hover:text-blue-800 dark:border-gray-600 dark:bg-gray-800 dark:text-blue-300 dark:hover:bg-gray-700 dark:hover:text-blue-200"
                                title={`Open PO ${clientPo}`}
                              >
                                {clientPo}
                              </Link>
                            ) : (
                              <div
                                className="truncate rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                                title={`${clientPo} — customer id required (use View on Clients).`}
                              >
                                {clientPo}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      <div className="mt-6 grid grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 rounded">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Clients</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{isLoading ? "—" : totalClients}</p>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 rounded">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Active Clients</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{isLoading ? "—" : activeClients}</p>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 rounded">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Active POs</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
            {isLoading || countsPending ? "…" : totalActivePos}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 rounded">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Revenue YTD</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">—</p>
        </div>
      </div>
    </div>
  );
}

export { Clients };
